import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import dynamic from 'next/dynamic';

import { FeatureFlags } from 'featureFlags/constants';
import { getProductRobotImages } from 'productTemplate/utils/getProductRobotImages';
import { getProductTemplateCarouselImages } from 'productTemplate/utils/getProductTemplateCarouselImages';
import { ImageGalleryDesktop } from 'productTemplate/components/imageGallery/ImageGalleryDesktop';
import { IRobotAsset } from 'shared/types/RobotAsset';
import { ProductTemplateDesktopFooterUpdated } from 'productTemplate/components/footer/ProductTemplateDesktopFooterUpdated';
import { RobotGallery } from 'productTemplate/components/robotGallery/RobotGallery';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreSelector } from 'store/hooks/storeHooks';

const SnackbarNotification = dynamic(
  () => import('shared/components/SnackbarNotification').then((mod) => mod.SnackbarNotification),
  {
    ssr: false,
  },
);

const PlaceOfferModal = dynamic(
  () =>
    import('productTemplate/components/windows/PlaceOfferModal').then((mod) => mod.PlaceOfferModal),
  { ssr: false },
);

const DeprecatedPlaceOfferModal = dynamic(
  () =>
    import('productTemplate/components/windows/DeprecatedPlaceOfferModal').then(
      (mod) => mod.DeprecatedPlaceOfferModal,
    ),
  { ssr: false },
);

export const ProductDetailPageDesktop = ({
  isAvailabilityRestricted,
  isFetchingBuyBarData,
}: {
  isAvailabilityRestricted: boolean;
  isFetchingBuyBarData?: boolean;
}) => {
  const { productTemplate } = useStoreSelector((state) => state.pdp);

  // console.log(productTemplate);
  const isOfferModalUpdatesEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_OFFER_MODAL_UPDATES,
  );
  const enablePdpImgAltTag = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PDP_IMAGE_ALT_TAG);
  const productTemplateImages = getProductTemplateCarouselImages({
    productTemplate,
    enablePdpImgAltTag,
  });
  const productRobotImages = getProductRobotImages(productTemplate);
  const {
    isSnackbarVisible: isOfferSnackbarVisible,
    setIsSnackbarVisible,
    snackbarMessage,
    isPlaceOfferModalShowing,
  } = useOffersContext();
  const [expandedImagesUrls, setExpandedImagesUrls] = useState<IRobotAsset[]>([]);
  const [zoomedInRobot, setZoomedInRobot] = useState(null);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);

  useEffect(() => {
    document.body.classList.add('hideOverflowX');
    document.documentElement.classList.add('hideOverflowX');
    return () => {
      document.documentElement.removeAttribute('class');
      document.body.removeAttribute('class');
    };
  }, []);

  return (
    <ProductDetailPageDesktop.Wrapper>
      {productRobotImages && (
        <RobotGallery
          expandedImagesUrls={expandedImagesUrls}
          robotList={productRobotImages}
          setExpandedImagesUrls={setExpandedImagesUrls}
          setZoomedInRobot={setZoomedInRobot}
          zoomedInRobot={zoomedInRobot}
        />
      )}
      {isOfferSnackbarVisible && (
        <SnackbarNotification
          setIsNotificationVisible={setIsSnackbarVisible}
          isNotificationVisible={isOfferSnackbarVisible}
        >
          {snackbarMessage}
        </SnackbarNotification>
      )}

      <ImageGalleryDesktop
        itemList={productTemplateImages}
        productType={productTemplate.productType}
        renderImagesInOrder={productTemplate.renderImagesInOrder}
        disableCarouselScroll={expandedImagesUrls.length > 0}
      />
      <ProductDetailPageDesktop.FlexContainer>
        <ProductDetailPageDesktop.Bounds id="pdp-wrapper" $isFooterExpanded={isFooterExpanded}>
          {isPlaceOfferModalShowing &&
            (isOfferModalUpdatesEnabled ? <PlaceOfferModal /> : <DeprecatedPlaceOfferModal />)}
        </ProductDetailPageDesktop.Bounds>
        <ProductTemplateDesktopFooterUpdated
          isAvailabilityRestricted={isAvailabilityRestricted}
          isFooterExpanded={isFooterExpanded}
          setIsFooterExpanded={setIsFooterExpanded}
          isFetchingBuyBarData={isFetchingBuyBarData}
        />
      </ProductDetailPageDesktop.FlexContainer>
    </ProductDetailPageDesktop.Wrapper>
  );
};

ProductDetailPageDesktop.Wrapper = styled.div`
  position: relative;
`;

ProductDetailPageDesktop.FlexContainer = styled.div`
  z-index: 300;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: fixed;
  width: 100vw;
  top: 50px;
  pointer-events: none;
`;

ProductDetailPageDesktop.Bounds = styled.div<{ $isFooterExpanded: boolean }>`
  height: calc(100vh - 130px);
  width: 100vw;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: end;

  ${({ $isFooterExpanded }) =>
    $isFooterExpanded &&
    css`
      height: calc(100vh - 280px);
    `}
`;
